<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import clubApplicationService from '../services/clubApplication.service'
import ClubApplicationAcceptModal from '../components/ClubApplicationAcceptModal.vue'
import ClubApplicationRejectModal from '../components/ClubApplicationRejectModal.vue'
import type { ClubApplicationDetail } from '../types/clubApplication.types'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')
const applicationUuid = computed(() => route.params.uuid as string)

const canManage = computed(() => (authStore.initData?.permission_model || []).includes('CLUB_APPLICATION_MANAGE'))

const application = ref<ClubApplicationDetail | null>(null)
const loading = ref(false)

const showAcceptModal = ref(false)
const showRejectModal = ref(false)

const isPending = computed(() => application.value?.status === 'pending')
const fullName = computed(() =>
  application.value ? `${application.value.first_name} ${application.value.last_name}` : ''
)

const age = computed(() => {
  if (!application.value?.birth_date) return null
  const birth = new Date(application.value.birth_date)
  const today = new Date()
  let years = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) years--
  return years
})

const fetchApplication = async () => {
  if (!clubUuid.value) return
  loading.value = true
  try {
    application.value = await clubApplicationService.getApplication(clubUuid.value, applicationUuid.value)
  } catch (err: any) {
    console.error('Fetch application error:', err)
    toastStore.error(err.response?.data?.message || t('clubApplications.errorFetchingOne'))
    router.push('/club-applications')
  } finally {
    loading.value = false
  }
}

const onDecisionDone = () => {
  showAcceptModal.value = false
  showRejectModal.value = false
  router.push('/club-applications')
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${formatDate(dateString)} ${hours}:${minutes}`
}

const hasAddress = computed(() => {
  const a = application.value
  return !!(a && (a.street || a.postal_code || a.city || a.school_name))
})

onMounted(() => {
  fetchApplication()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/club-applications" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('clubApplications.backToList') }}
        </router-link>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <template v-else-if="application">
        <!-- Header with Actions -->
        <div class="mb-4 flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ fullName }}</h1>
            <p class="text-sm text-gray-600">
              <span v-if="age !== null">{{ age }} {{ t('playerDetail.yearsOld') }} · </span>
              {{ t('clubApplications.registrationDate') }}: {{ formatDateTime(application.created_at) }}
            </p>
          </div>
          <div v-if="canManage && isPending" class="flex gap-2">
            <button
              @click="showAcceptModal = true"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600"
            >
              {{ t('clubApplications.accept') }}
            </button>
            <button
              @click="showRejectModal = true"
              class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 border-2 border-red-600"
            >
              {{ t('clubApplications.reject') }}
            </button>
          </div>
        </div>

        <!-- Status banner for already processed applications -->
        <div v-if="!isPending" class="bg-gray-100 border-2 border-gray-300 rounded p-3 mb-4">
          <span class="text-sm font-medium text-gray-800">
            {{ application.status === 'accepted' ? t('clubApplications.statusAccepted') : t('clubApplications.statusRejected') }}
          </span>
        </div>

        <!-- Activation banner -->
        <div v-if="application.is_activated" class="bg-green-50 border-2 border-green-300 rounded p-3 mb-4">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm font-medium text-green-800">
              {{ t('clubApplications.activatedBanner', { date: formatDateTime(application.activated_at) }) }}
            </span>
          </div>
        </div>
        <div v-else class="bg-red-50 border-2 border-red-300 rounded p-3 mb-4">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm font-medium text-red-800">{{ t('clubApplications.notActivatedBanner') }}</span>
          </div>
        </div>

        <!-- Possible duplicate banner -->
        <div v-if="application.possible_duplicate" class="bg-amber-50 border-2 border-amber-300 rounded p-3 mb-4">
          <span class="text-sm font-medium text-amber-800">{{ t('clubApplications.possibleDuplicateBanner') }}</span>
        </div>

        <!-- Data - 3 columns -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <!-- Column 1: Basic data + guardians -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.basicData') }}</h2>
              <div class="space-y-2 text-sm">
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.gender') }}</span>
                  <span class="ml-2 font-medium">{{ application.gender === 'M' ? t('playerDetail.male') : t('playerDetail.female') }}</span>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.birthDate') }}</span>
                  <span class="ml-2 font-medium">{{ formatDate(application.birth_date) }}</span>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.licenseNumber') }}</span>
                  <span class="ml-2 font-medium">{{ application.license_number || '-' }}</span>
                </div>
              </div>
            </div>

            <div v-if="application.guardians && application.guardians.length > 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">
                {{ application.guardian_type === 'guardian' ? t('playerDetail.legalGuardians') : t('playerDetail.parents') }}
              </h2>
              <div class="space-y-2 text-sm">
                <div v-for="(guardian, index) in application.guardians" :key="index">
                  <span class="text-gray-600">{{ index + 1 }}.</span>
                  <span class="ml-2 font-medium">{{ guardian.first_name }} {{ guardian.last_name }}</span>
                  <span v-if="guardian.phone" class="ml-2 text-gray-700">(tel. {{ guardian.phone }})</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Column 2: Contact -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.contactData') }}</h2>
              <div class="space-y-2 text-sm">
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.email') }}</span>
                  <span class="ml-2 font-medium break-all">{{ application.email }}</span>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.phone') }}</span>
                  <span class="ml-2 font-medium">{{ application.phone || '-' }}</span>
                </div>
                <div v-if="application.terms_accepted_at">
                  <span class="text-gray-600">{{ t('clubApplications.termsAcceptedAt') }}</span>
                  <span class="ml-2 font-medium">{{ formatDateTime(application.terms_accepted_at) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Column 3: Address -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('playerDetail.addressAndSchool') }}</h2>
              <div v-if="hasAddress" class="space-y-2 text-sm">
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.street') }}</span>
                  <span class="ml-2 font-medium">{{ application.street || '-' }}</span>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.city') }}</span>
                  <span class="ml-2 font-medium">{{ [application.postal_code, application.city].filter(Boolean).join(' ') || '-' }}</span>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('playerDetail.school') }}</span>
                  <span class="ml-2 font-medium">{{ application.school_name || '-' }}</span>
                </div>
              </div>
              <p v-else class="text-sm text-gray-500">{{ t('playerDetail.noAddressData') }}</p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </main>

  <ClubApplicationAcceptModal
    :show="showAcceptModal"
    :club-uuid="clubUuid"
    :application-uuids="application ? [application.uuid] : []"
    :not-activated-count="application && !application.is_activated ? 1 : 0"
    @close="showAcceptModal = false"
    @done="onDecisionDone"
  />

  <ClubApplicationRejectModal
    :show="showRejectModal"
    :club-uuid="clubUuid"
    :application-uuids="application ? [application.uuid] : []"
    :name="fullName"
    @close="showRejectModal = false"
    @done="onDecisionDone"
  />
</template>
